import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintWithGlyph, HoverHintContent } from "@/components/ui/title-help";
import { buildMrpSkuDetailPath, type MrpAmlEntry } from "@/lib/mrp";
import {
  AML_ACTIVE_COLUMN_HINT,
  formatAmlAlternateLabel,
  isPackSizeAmlRow,
  sortAmlAlternateRows,
} from "@/pages/mrp/aml/amlEntryUtils";

export type SkuStandardPackSizesSectionProps = {
  sku: string;
  amlRows: MrpAmlEntry[];
  amlPackDrafts: Record<number, string>;
  onAmlPackDraftChange: (amlId: number, value: string) => void;
  disabled?: boolean;
  hubWritesAllowed?: boolean;
};

export function SkuStandardPackSizesSection({
  sku,
  amlRows,
  amlPackDrafts,
  onAmlPackDraftChange,
  disabled = false,
  hubWritesAllowed = true,
}: SkuStandardPackSizesSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const packSizeRows = sortAmlAlternateRows(amlRows.filter(isPackSizeAmlRow));

  const createSelfMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ row: MrpAmlEntry; created: boolean }>(
        "/api/mrp/skus/aml-self",
        { method: "POST", query: { sku } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-800">Standard pack sizes</h3>
        <HoverHintWithGlyph
          hoverHint={
            "Whole units per supplier pack or reel, stored on each AML line in public.approved_components. Until the first real AML line exists, the hub may add a blank self placeholder row for pack size and sourcing. After you add catalog or custom-manufactured alternates, that placeholder is removed automatically."
          }
        />
      </div>
      {packSizeRows.length === 0 ? (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-3 text-sm text-amber-950">
          <p>
            No AML line for this CPN yet. Add catalog or custom-manufactured alternates on the{" "}
            <Link
              className="font-medium text-sky-800 underline hover:text-sky-950"
              to={buildMrpSkuDetailPath(sku, { tab: "aml" })}
            >
              AML detail
            </Link>{" "}
            tab, then set pack size here.
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!hubWritesAllowed || disabled || createSelfMutation.isPending}
            isLoading={createSelfMutation.isPending}
            onClick={() => createSelfMutation.mutate()}
          >
            Add self AML line
          </Button>
          {createSelfMutation.isError ? (
            <p className="text-sm text-red-700">
              {createSelfMutation.error instanceof Error
                ? createSelfMutation.error.message
                : "Could not add self AML line."}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">AML line</th>
                <th className="px-3 py-2 text-right">Pack size</th>
              </tr>
            </thead>
            <tbody>
              {packSizeRows.map((row) => {
                const draft = amlPackDrafts[row.id] ?? "";
                const label = formatAmlAlternateLabel(row, sku);
                return (
                  <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-3 py-2 text-slate-900">
                      {label}
                      {!row.active ? (
                        <HoverHintContent hoverHint={AML_ACTIVE_COLUMN_HINT}>
                          <span className="ml-1 cursor-help text-xs font-normal italic text-amber-900">
                            inactive
                          </span>
                        </HoverHintContent>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col items-end gap-1">
                        <Label htmlFor={`aml-pack-size-${row.id}`} className="sr-only">
                          Pack size for {label}
                        </Label>
                        <Input
                          id={`aml-pack-size-${row.id}`}
                          type="number"
                          min={1}
                          step={1}
                          required
                          disabled={disabled}
                          className="w-28 text-right font-mono"
                          value={draft}
                          onChange={(event) => onAmlPackDraftChange(row.id, event.target.value)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Build initial AML pack-size draft strings from loaded rows. */
export function initialAmlPackSizeDrafts(rows: MrpAmlEntry[]): Record<number, string> {
  const drafts: Record<number, string> = {};
  for (const row of rows) {
    if (!isPackSizeAmlRow(row)) continue;
    if (row.standard_pack_size != null && row.standard_pack_size >= 1) {
      drafts[row.id] = String(row.standard_pack_size);
    }
  }
  return drafts;
}

export function useAmlPackSizeDrafts(sku: string): {
  amlPackDrafts: Record<number, string>;
  setAmlPackDraft: (amlId: number, value: string) => void;
  amlRowsQuery: ReturnType<typeof useQuery<{ rows: MrpAmlEntry[] }, ApiError>>;
} {
  const amlRowsQuery = useQuery<{ rows: MrpAmlEntry[] }, ApiError>({
    queryKey: ["mrp", "aml", "cpn", sku],
    queryFn: () =>
      apiFetch<{ rows: MrpAmlEntry[] }>("/api/mrp/aml", {
        query: { cpn: sku, include_inactive: "true" },
      }),
    enabled: sku.length > 0,
  });

  const [amlPackDrafts, setAmlPackDrafts] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!amlRowsQuery.data?.rows) return;
    setAmlPackDrafts(initialAmlPackSizeDrafts(amlRowsQuery.data.rows));
  }, [amlRowsQuery.data?.rows]);

  return {
    amlPackDrafts,
    setAmlPackDraft: (amlId, value) => {
      setAmlPackDrafts((previous) => ({ ...previous, [amlId]: value }));
    },
    amlRowsQuery,
  };
}

export function buildAmlStandardPackSizePatches(
  rows: MrpAmlEntry[],
  drafts: Record<number, string>,
  cpn: string,
): { id: number; standard_pack_size: number }[] {
  const patches: { id: number; standard_pack_size: number }[] = [];
  for (const row of rows) {
    if (!isPackSizeAmlRow(row)) continue;
    const raw = drafts[row.id] ?? "";
    const trimmed = raw.trim();
    const label = formatAmlAlternateLabel(row, cpn);
    if (trimmed === "") {
      throw new Error(`Pack size is required for ${label}.`);
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isInteger(parsed) || parsed < 1) {
      throw new Error(`Pack size for ${label} must be a positive integer.`);
    }
    const prev =
      row.standard_pack_size != null && row.standard_pack_size >= 1 ? row.standard_pack_size : null;
    if (parsed !== prev) {
      patches.push({ id: row.id, standard_pack_size: parsed });
    }
  }
  return patches;
}

/** True when any pack-size draft differs from the saved AML row (does not validate). */
export function amlStandardPackSizesAreDirty(
  rows: MrpAmlEntry[],
  drafts: Record<number, string>,
): boolean {
  for (const row of rows) {
    if (!isPackSizeAmlRow(row)) continue;
    const draft = (drafts[row.id] ?? "").trim();
    const previous =
      row.standard_pack_size != null && row.standard_pack_size >= 1
        ? String(row.standard_pack_size)
        : "";
    if (draft !== previous) return true;
  }
  return false;
}
